import { MapPin } from 'lucide-react'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'

/**
 * ComplaintsPage — Feedback & Complaints (footer-only, regulatory).
 *
 * Required under ASIC Regulatory Guide 271 (RG 271), which sets out the Internal
 * Dispute Resolution (IDR) standards for financial firms, and mandates that
 * customers be told how to escalate to the Australian Financial Complaints
 * Authority (AFCA) — the external EDR body. Copy is a launch-ready draft for
 * Chelston Alvares' sign-off.
 */

const IDR_STEPS = [
  {
    n: '1',
    title: 'Tell us',
    body: 'Contact your Greystone broker or our office by phone, email or post. Let us know what went wrong and how you would like it resolved.',
  },
  {
    n: '2',
    title: 'We acknowledge',
    body: 'We will acknowledge your complaint promptly, normally within one business day, and give you a point of contact who will own it through to resolution.',
  },
  {
    n: '3',
    title: 'We resolve',
    body: 'We aim to resolve complaints quickly and fairly. Under ASIC RG 271 we will give you a written response within 30 days, explaining the outcome and your right to escalate.',
  },
]

export default function ComplaintsPage() {
  return (
    <>
      <Seo
        title="Complaints & Dispute Resolution"
        path="/complaints"
        description="How to make a complaint and our dispute-resolution process, including your right to escalate to AFCA."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-4xl mx-auto px-6 pb-12">
          <div className="inline-flex items-center gap-2 border border-[#E8E8E2] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gs-text-soft mb-6">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tenantConfig.accentColor }} />
            Regulatory · Complaints
          </div>
          <h1 className="font-heading text-3xl md:text-5xl text-ink font-semibold leading-tight mb-4">
            Feedback &amp; complaints
          </h1>
          <p className="text-lg text-gs-text-soft">
            We want to hear from you if something has not met your expectations. Every complaint helps
            us improve, and you have the right to have it handled fairly. {tenantConfig.name} follows the
            Internal Dispute Resolution (IDR) standards set by ASIC Regulatory Guide 271.
          </p>
        </section>

        {/* IDR PROCESS */}
        <section className="max-w-4xl mx-auto px-6 pb-12">
          <h2 className="font-heading text-2xl md:text-3xl text-ink mb-2">
            Our internal dispute resolution process
          </h2>
          <p className="text-gs-text-soft mb-6">
            Most concerns are resolved quickly once we know about them. Here is what to expect when you
            raise a complaint with us directly.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {IDR_STEPS.map((s) => (
              <div key={s.n} className="p-6 rounded-2xl border border-[#E8E8E2] bg-white">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold mb-3"
                  style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
                >
                  {s.n}
                </div>
                <h3 className="font-heading font-semibold text-ink mb-2">{s.title}</h3>
                <p className="text-sm text-gs-text-soft">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-[#E8E8E2] bg-[#FAFAF7] p-6 text-sm">
            <div className="font-heading font-semibold text-ink mb-2">Lodge a complaint with us</div>
            <p className="text-gs-text-soft mb-5">
              You can make a complaint verbally or in writing by contacting your {tenantConfig.name}{' '}
              broker directly, or by using any of the following:
            </p>
            <dl className="grid gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-gs-text-muted uppercase tracking-wide text-xs mb-1">Email</dt>
                <dd>
                  <a href="mailto:resolutions@lmg.broker" className="text-ink underline decoration-1 underline-offset-2 break-words">resolutions@lmg.broker</a>
                </dd>
              </div>
              <div>
                <dt className="text-gs-text-muted uppercase tracking-wide text-xs mb-1">Phone</dt>
                <dd>
                  <a href="tel:1800275564" className="text-ink underline decoration-1 underline-offset-2">1800 275 564</a>
                  <span className="block text-gs-text-muted text-xs mt-0.5">Toll free · Mon–Fri 9am–5pm AEST</span>
                </dd>
              </div>
              <div>
                <dt className="text-gs-text-muted uppercase tracking-wide text-xs mb-1">Mail</dt>
                <dd className="text-gs-text-soft">
                  Resolutions, Level 28, 35 Collins Street, Melbourne VIC 3000
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Level+28%2C+35+Collins+Street%2C+Melbourne+VIC+3000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-ink underline decoration-1 underline-offset-2 mt-1"
                  >
                    <MapPin size={13} /> View on map
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </section>

        {/* AFCA — EXTERNAL EDR */}
        <section className="max-w-4xl mx-auto px-6 pb-12">
          <div className="rounded-3xl p-8 md:p-10" style={{ background: tenantConfig.primaryColor }}>
            <h2 className="font-heading text-2xl md:text-3xl mb-3" style={{ color: tenantConfig.accentColor }}>
              Still not resolved? Contact AFCA
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl">
              If we cannot resolve your complaint to your satisfaction, or you have not heard back within
              30 days, you can refer it, free of charge, to the Australian Financial Complaints
              Authority (AFCA). AFCA is the independent external dispute resolution (EDR) scheme that
              provides fair and independent financial services complaint resolution.
            </p>
            <dl className="grid gap-4 sm:grid-cols-2 text-sm">
              <div>
                <dt className="text-white/50 uppercase tracking-wide text-xs mb-1">Phone</dt>
                <dd><a href="tel:1800931678" className="text-white hover:opacity-80">1800 931 678</a></dd>
              </div>
              <div>
                <dt className="text-white/50 uppercase tracking-wide text-xs mb-1">Email</dt>
                <dd><a href="mailto:info@afca.org.au" className="text-white hover:opacity-80">info@afca.org.au</a></dd>
              </div>
              <div>
                <dt className="text-white/50 uppercase tracking-wide text-xs mb-1">Website</dt>
                <dd><a href="https://www.afca.org.au" target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-80">www.afca.org.au</a></dd>
              </div>
              <div>
                <dt className="text-white/50 uppercase tracking-wide text-xs mb-1">Post</dt>
                <dd className="text-white/90">GPO Box 3, Melbourne VIC 3001</dd>
              </div>
            </dl>
          </div>
        </section>

        {/* DISCLAIMER */}
        <section className="max-w-3xl mx-auto px-6">
          <p className="text-xs text-gs-text-muted text-center">
            General information only. Timeframes reflect ASIC RG 271 maximum response periods and may be
            shorter in practice.
          </p>
        </section>
      </main>
      <Footer />
    </>
  )
}
